/**
 * llmService.js — client wrapper for the callLLMAPI callable.
 * Shaped by: CM7-03; decision D-CM7-20.
 * Invariants: fallbackPromptId is omitted from the payload unless the caller sets it.
 * Related: functions/src/llm/callLLMAPI.js.
 */
import { getFunctions, httpsCallable } from "firebase/functions";

const functions = getFunctions();

const callLLMAPICallable = httpsCallable(functions, "callLLMAPI", {
  timeout: 300000,
});

export async function invokeLLMPrompt({
  promptId,
  userMessage,
  config,
  systemPrompt,
  organizationId,
  regionId,
  tciId,
  variantSlug,
  preview,
  includeEmbeddedDocuments,
  embeddedDocumentsOptions,
  fallbackPromptId,
}) {
  const payload = {
    promptId,
    userMessage,
  };

  if (config && typeof config === "object") payload.config = config;
  if (typeof systemPrompt === "string" && systemPrompt.trim()) {
    payload.systemPrompt = systemPrompt;
  }
  if (typeof organizationId === "string" && organizationId.trim()) {
    payload.organizationId = organizationId;
  }
  if (typeof regionId === "string" && regionId.trim()) {
    payload.regionId = regionId;
  }
  if (typeof tciId === "string" && tciId.trim()) {
    payload.tciId = tciId;
  }
  if (typeof variantSlug === "string" && variantSlug.trim()) {
    payload.variantSlug = variantSlug.trim();
  }
  if (preview && typeof preview === "object") {
    payload.preview = preview;
  }
  if (includeEmbeddedDocuments === true) {
    payload.includeEmbeddedDocuments = true;
  }
  if (embeddedDocumentsOptions && typeof embeddedDocumentsOptions === "object") {
    payload.embeddedDocumentsOptions = embeddedDocumentsOptions;
  }
  if (typeof fallbackPromptId === "string" && fallbackPromptId.trim()) {
    payload.fallbackPromptId = fallbackPromptId.trim();
  }

  const startedAt = performance.now();
  console.log("[callLLMAPI] →", {
    promptId,
    userMessageLength: typeof userMessage === "string" ? userMessage.length : 0,
    organizationId: payload.organizationId || null,
    hasSystemPrompt: !!payload.systemPrompt,
    hasPreview: !!payload.preview,
  });

  let result;
  try {
    result = await callLLMAPICallable(payload);
  } catch (err) {
    const ms = Math.round(performance.now() - startedAt);
    console.error("[callLLMAPI] ✗", { promptId, ms, error: err?.message || err });
    throw err;
  }

  const ms = Math.round(performance.now() - startedAt);
  const output = result?.data?.output;

  const meta = result?.data?.meta || null;
  console.log("[callLLMAPI] ←", {
    promptId,
    ms,
    outputLength: typeof output === "string" ? output.length : 0,
    hasMeta: !!meta,
    systemPromptLength: meta?.systemPromptLength ?? null,
    systemPromptSectionCount: meta?.systemPromptSectionCount ?? null,
    temperature: meta?.temperature ?? null,
    model: meta?.model ?? null,
  });
  if (meta?.systemPromptHead) {
    console.log(
      `[callLLMAPI] system prompt head (${promptId}):\n${meta.systemPromptHead}`,
    );
  }
  if (meta?.systemPromptTail) {
    console.log(
      `[callLLMAPI] system prompt tail (${promptId}):\n${meta.systemPromptTail}`,
    );
  }

  if (!output || typeof output !== "string") {
    throw new Error("callLLMAPI returned no output");
  }

  return {
    output,
    meta: result?.data?.meta || null,
    embeddedDocuments: result?.data?.meta?.embeddedDocuments || null,
    raw: result?.data || null,
  };
}

